// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    CheckoutStep,
    CheckoutStepKey,
} from "@hardwoods/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmit.helper";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Dictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type CheckoutState = {
    currentStep?: CheckoutStep;
    nextStepHash?: CheckoutStepKey;
    validatePayment?: () => Promise<boolean>;
    goToPreviousStep?: () => void;
    goToNextStep?: () => void;
    userDataById: Dictionary<CheckoutUserData>;
    isExpressCheckoutCalled?: boolean;
    firstAvailableDate?: string;
    blackoutDays?: [];
    shipComplete?: string;
};

export interface CheckoutUserData {
    paymentMethod?: string;
    poNumber?: string;
    job?: string;
    saveCard?: boolean;
    cardHolderNickname?: string;
    cardHolderName?: string;
    cardType?: string;
    expirationMonth?: number;
    expirationYear?: number;
    useBillingAddress?: boolean;
    address1?: string;
    countryId?: string;
    stateId?: string;
    city?: string;
    postalCode?: string;
}

const initialState: CheckoutState = {
    userDataById: {},
};

const xCheckout = createTypedReducerWithImmer(initialState, {
    "xCheckoutState/SetCurrentStep": (draft: Draft<CheckoutState>, action: { currentStep: CheckoutStep }) => {
        draft.currentStep = action.currentStep;
    },
    "xCheckoutState/SetNextCheckoutStep": (
        draft: Draft<CheckoutState>,
        action: {
            nextStepHash: CheckoutStepKey;
        },
    ) => {
        draft.nextStepHash = action.nextStepHash;
    },
    "xCheckoutState/SetNextPreviousHandlers": (
        draft: Draft<CheckoutState>,
        action: { goToPreviousStep?: () => void; goToNextStep?: () => void },
    ) => {
        draft.goToPreviousStep = action.goToPreviousStep;
        draft.goToNextStep = action.goToNextStep;
    },
    "xCheckoutState/SetPaymentValidation": (
        draft: Draft<CheckoutState>,
        action: { validate?: () => Promise<boolean> },
    ) => {
        draft.validatePayment = action.validate;
    },
    "xCheckoutState/SetUserData": (
        draft: Draft<CheckoutState>,
        action: { userData: CheckoutUserData; cartId: string },
    ) => {
        const currentUserData = draft.userDataById[action.cartId] || {};
        draft.userDataById[action.cartId] = mergeToNew(currentUserData, action.userData);
    },
    "xCheckoutState/Reset": (draft: Draft<CheckoutState>) => {
        draft.currentStep = undefined;
        draft.nextStepHash = undefined;
        draft.validatePayment = undefined;
        draft.goToPreviousStep = undefined;
        draft.goToNextStep = undefined;
        draft.shipComplete = undefined;
    },
    "xCheckoutState/CompleteExpressCheckout": (draft: Draft<CheckoutState>) => {
        draft.isExpressCheckoutCalled = true;
    },
    "xCheckoutState/LoadFirstAvailableDate": (
        draft: Draft<CheckoutState>,
        action: { firstAvailableDate: string },
    ) => {
        draft.firstAvailableDate = action.firstAvailableDate;
    },
    "xCheckoutState/LoadBlackOutDays": (
        draft: Draft<CheckoutState>,
        action: { blackoutDays: [] },
    ) => {
        draft.blackoutDays = action.blackoutDays;
    },
    "xCheckoutState/SetShipComplete": (draft: Draft<CheckoutState>, action: { shipComplete: string }) => {
        draft.shipComplete = action.shipComplete;
    },
});

export type xCheckoutState = {
    xCheckout: CheckoutState;
} & ApplicationState;

type xCheckoutReducers = {
    xCheckout: typeof xCheckout;
} & typeof reducers;

(reducers as xCheckoutReducers).xCheckout = xCheckout;
